import { useEffect, useState } from "react";
import {
  addMinutes,
  differenceInMinutes,
  startOfDay,
  startOfToday,
  isAfter,
  format,
  parse,
  getMonth,
  getYear,
} from "date-fns";

import { Box, Stack, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AssignmentIcon from "@mui/icons-material/Assignment";

import useCall from "../../hooks/useCall";
import useData from "../../hooks/useData";
import useLocalization from "../../hooks/useLocalization";
import useSurvey from "../../hooks/useSurvey";
import { getId } from "../../utils/idHelpers";

import SurveyEntryModal from "../modals/SurveyEntryModal";
import UserSelectionModal from "../modals/UserSelectionModal";

import EditorToolbar from "../editors/EditorToolbar";
import DateField from "../elements/Date";
import Time from "../elements/Time";
import Input from "../elements/Input";
import Switch from "../elements/Switch";
import SurveyStatus from "./SurveyStatus";
import Dialog_YesNo from "./Dialog_YesNo";
import CallStatus from "./CallStatus";

const CallDocket = ({ call, onRefresh, onClose, readOnly = false }) => {
  const theme = useTheme();
  const { updateCall, updateCallStatus, deleteCall } = useCall();
  const { getFromServer, updateRecord } = useData();
  const { getSurveyStatus } = useSurvey();
  const { callStatus, getLabel } = useLocalization();

  const [data, setData] = useState();
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [openSurveyEntry, setOpenSurveyEntry] = useState(false);
  const [refreshSurveys, setRefreshSurveys] = useState(0);
  const [surveyStatusOverrides, setSurveyStatusOverrides] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);

  const toValidDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : new Date(value);
    }

    if (typeof value === "string") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const normalizeTimeValue = (value) => {
    const parsed = toValidDate(value);
    return parsed ? parsed.getTime() : null;
  };

  const normalizeNumberValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const applyBaseDate = (value, baseDate) => {
    const dateValue = toValidDate(value);
    const base = toValidDate(baseDate);
    if (!dateValue || !base) return dateValue;

    const merged = new Date(base);
    merged.setHours(
      dateValue.getHours(),
      dateValue.getMinutes(),
      dateValue.getSeconds(),
      dateValue.getMilliseconds(),
    );

    return merged;
  };

  useEffect(() => {
    if (!call) return; // Guard clause - don't initialize if call is undefined

    const getScheduleDate = () => {
      if (call?.yyyymmdd) {
        if (call.yyyymmdd instanceof Date) {
          return call.yyyymmdd;
        }

        if (typeof call.yyyymmdd === "string") {
          const parsed = parse(call.yyyymmdd, "yyyy-MM-dd", new Date());
          if (!Number.isNaN(parsed.getTime())) {
            return parsed;
          }
        }
      }

      if (call?.scheduleDate instanceof Date) {
        return call.scheduleDate;
      }

      if (typeof call?.scheduleDate === "string") {
        const parsedScheduleDate = new Date(call.scheduleDate);
        if (!Number.isNaN(parsedScheduleDate.getTime())) {
          return parsedScheduleDate;
        }
      }

      return null;
    };

    const scheduleDate = getScheduleDate();

    let startedAt = call?.startedAt
      ? applyBaseDate(call.startedAt, scheduleDate)
      : scheduleDate
        ? new Date(scheduleDate)
        : new Date();
    if (call?.callStatus === "future") startedAt = "";

    const finishedAt = call?.finishedAt
      ? applyBaseDate(call.finishedAt, scheduleDate)
      : "";

    setData({
      id: call.id || call._id,
      scheduleDate,
      startedAt: startedAt,
      finishedAt,
      callDuration: call?.callDuration ? call.callDuration : "",
      firstCall: call?.firstCall ? call.firstCall : false,
      lastCall: call?.lastCall ? call.lastCall : false,
      mileage: call?.mileage ? call.mileage : "",
      homeMileage:
        call?.homeMileage !== null && call?.homeMileage !== undefined
          ? call.homeMileage
          : "",
      isLocked: call?.isLocked || false,
      payrollLocked: call?.payrollLocked || false,
      repNotes: call?.repNotes || "",
      hourlyRate: call?.hourlyRate || "",
      estCallDuration: call?.project?.callDuration || 0,
      callVariance: call?.project?.callVariance || 0,
      isBilled: call?.isBilled || false,
      excludeFromPayroll: call?.excludeFromPayroll || false,
      excludeFromBilling: call?.excludeFromBilling || false,
      userId: call?.userId || null,
      repName: call?.repName || "",
      syncLocked: call?.syncLocked === true,
    });

    setSelectedSurveyId(null);
    setSurveyStatusOverrides({});
  }, [call]);

  useEffect(() => {
    if (!call?.id || !Array.isArray(call?.surveys) || call.surveys.length === 0)
      return;

    let cancelled = false;

    const toTimeValue = (value) => {
      const date = new Date(value || 0);
      const time = date.getTime();
      return Number.isNaN(time) ? 0 : time;
    };

    const recalculateSurveyStatuses = async () => {
      const callId = String(getId(call) || "");
      if (!callId) return;

      const isLockedCall =
        call?.isLocked === true || Number(call?.isLocked ?? 0) === 1;
      const isPayrollLockedCall =
        call?.payrollLocked === true || Number(call?.payrollLocked ?? 0) === 1;

      if (isLockedCall || isPayrollLockedCall) {
        return;
      }

      const todayYyyymmdd = format(startOfToday(), "yyyy-MM-dd");
      const isFutureCall =
        typeof call?.yyyymmdd === "string" && call.yyyymmdd > todayYyyymmdd;

      const nextOverrides = {};

      for (const survey of call.surveys) {
        const surveyId = String(survey?.id || survey?._id || "");
        if (!surveyId) continue;

        const callResultRows = await getFromServer({
          entityName: "CallResult",
          filter: { callId, surveyId },
        });

        if (!Array.isArray(callResultRows) || callResultRows.length === 0) {
          continue;
        }

        const latestCallResult = [...callResultRows].sort((left, right) => {
          const leftTime = Math.max(
            toTimeValue(left?.updatedAt),
            toTimeValue(left?.finishedAt),
            toTimeValue(left?.createdAt),
          );
          const rightTime = Math.max(
            toTimeValue(right?.updatedAt),
            toTimeValue(right?.finishedAt),
            toTimeValue(right?.createdAt),
          );
          return rightTime - leftTime;
        })[0];

        const surveyAnswers = latestCallResult?.id
          ? await getFromServer({
              entityName: "SurveyAnswer",
              filter: { callResultId: latestCallResult.id },
            })
          : [];

        const surveyStatusCalc = await getSurveyStatus({
          call,
          surveyId,
          answers: Array.isArray(surveyAnswers) ? surveyAnswers : [],
        });

        const persistedStatus =
          `${latestCallResult?.surveyStatus || ""}`.trim();

        if (persistedStatus === "complete") {
          nextOverrides[surveyId] = "complete";
          continue;
        }

        const normalizedSurveyStatus = isFutureCall
          ? "future"
          : surveyStatusCalc?.completed
            ? "complete"
            : "incomplete";

        nextOverrides[surveyId] = normalizedSurveyStatus;

        if (latestCallResult?.surveyStatus !== normalizedSurveyStatus) {
          await updateRecord(
            "CallResult",
            {
              ...latestCallResult,
              surveyStatus: normalizedSurveyStatus,
            },
            false,
          );
        }
      }

      if (cancelled) return;

      if (Object.keys(nextOverrides).length > 0) {
        setSurveyStatusOverrides((current) => ({
          ...current,
          ...nextOverrides,
        }));
      }

      await updateCallStatus(callId);
    };

    recalculateSurveyStatuses().catch((err) => {
      console.error("CallDocket.recalculateSurveyStatuses", err);
    });

    return () => {
      cancelled = true;
    };
  }, [call?.id, refreshSurveys]);

  const handleSave = async () => {
    const protectedFieldsChanged =
      normalizeTimeValue(data?.startedAt) !==
        normalizeTimeValue(call?.startedAt) ||
      normalizeTimeValue(data?.finishedAt) !==
        normalizeTimeValue(call?.finishedAt) ||
      normalizeNumberValue(data?.callDuration) !==
        normalizeNumberValue(call?.callDuration) ||
      normalizeNumberValue(data?.mileage) !==
        normalizeNumberValue(call?.mileage) ||
      normalizeNumberValue(data?.homeMileage) !==
        normalizeNumberValue(call?.homeMileage);

    const currentSyncLocked = call?.syncLocked === true;
    const requestedSyncLocked = data?.syncLocked === true;
    const syncLockChangedByUser = requestedSyncLocked !== currentSyncLocked;

    const payload = {
      ...data,
      homeMileage:
        data?.homeMileage === "" || data?.homeMileage === undefined
          ? null
          : data.homeMileage,
    };

    if (syncLockChangedByUser) {
      payload.syncLocked = requestedSyncLocked;
    } else if (protectedFieldsChanged) {
      payload.syncLocked = true;
    } else {
      delete payload.syncLocked;
    }

    await updateCall(payload);
    onRefresh();
  };

  const handleChange = (field, value) => {
    setData((current) => {
      let newValue = value;
      let additionalFields = {};

      if (field === "callDuration" && value) newValue = parseInt(value);
      if (field === "mileage" && value) newValue = parseInt(value);
      if (field === "homeMileage") {
        if (value === "" || value === null || value === undefined) {
          newValue = "";
        } else {
          const parsed = parseFloat(value);
          newValue = Number.isNaN(parsed) ? "" : parsed;
        }
      }

      if (field === "finishedAt" || field === "startedAt") {
        newValue = applyBaseDate(value, current?.scheduleDate);

        if (value) {
          let duration = 0;
          if (field === "finishedAt") {
            duration = differenceInMinutes(newValue, current.startedAt);
          } else if (current.finishedAt) {
            duration = differenceInMinutes(current.finishedAt, newValue);
          }

          if (duration < 0) {
            duration = "";
            newValue = "";
          }

          additionalFields.callDuration = duration;
        } else {
          additionalFields.callDuration = "";
        }
      }

      if (field === "callDuration") {
        if (value && value !== "0") {
          newValue = parseInt(value);
          if (newValue > 0) {
            let finishedAt = addMinutes(current.startedAt, newValue);
            additionalFields.finishedAt = finishedAt;
          } else {
            additionalFields.finishedAt = "";
            newValue = "";
          }
        } else {
          additionalFields.finishedAt = "";
          newValue = "";
        }
      }

      if (field === "scheduleDate") {
        newValue = startOfDay(newValue);
        additionalFields.scheduleYear = getYear(newValue);
        additionalFields.scheduleMonth = getMonth(newValue);
        additionalFields.yyyymmdd = format(newValue, "yyyy-MM-dd");

        if (current?.startedAt) {
          additionalFields.startedAt = applyBaseDate(
            current.startedAt,
            newValue,
          );
        }
        if (current?.finishedAt) {
          additionalFields.finishedAt = applyBaseDate(
            current.finishedAt,
            newValue,
          );
        }
      }

      return { ...current, [field]: newValue, ...additionalFields };
    });
  };

  const handleOpenSurveyEntry = (surveyId) => {
    const callId = String(getId(call) || "");

    setSelectedSurveyId(surveyId);
    setOpenSurveyEntry(true);
  };

  const handleCloseSurveyEntry = async (changesMade, surveyStatus) => {
    const closingSurveyId = selectedSurveyId;
    const callId = String(getId(call) || "");

    await updateCallStatus(callId);

    if (closingSurveyId) {
      const callResultRows = await getFromServer({
        entityName: "CallResult",
        filter: { callId, surveyId: closingSurveyId },
      });

      const persistedSurveyStatus = Array.isArray(callResultRows)
        ? callResultRows.some((row) => row?.surveyStatus === "complete")
          ? "complete"
          : callResultRows[0]?.surveyStatus
        : null;

      // Prevent stale reads from downgrading a just-completed survey on close.
      const latestSurveyStatus =
        surveyStatus === "complete" || persistedSurveyStatus === "complete"
          ? "complete"
          : surveyStatus || persistedSurveyStatus;

      if (latestSurveyStatus) {
        setSurveyStatusOverrides((current) => ({
          ...current,
          [closingSurveyId]: latestSurveyStatus,
        }));
      }
    }

    setOpenSurveyEntry(false);
    setSelectedSurveyId(null);
    setRefreshSurveys((current) => current + 1);
    // onRefresh();
  };

  const handleStartCall = () => {
    const base = data?.scheduleDate || new Date();
    const now = new Date();
    now.setFullYear(base.getFullYear(), base.getMonth(), base.getDate());
    handleChange("startedAt", now);
  };

  const handleFinishCall = () => {
    const base = data?.scheduleDate || new Date();
    const now = new Date();
    now.setFullYear(base.getFullYear(), base.getMonth(), base.getDate());
    handleChange("finishedAt", now);
  };

  const handleCustomOption = (action) => {
    if (action === "delete") {
      setOpenDeleteDialog(true);
    }
  };

  const handleDelete_Yes = async () => {
    setOpenDeleteDialog(false);
    const callId = String(getId(call) || data?.id || "").trim();
    if (!callId) {
      console.error("CallDocket.handleDelete_Yes missing call id", { call });
      return;
    }

    const results = await deleteCall(callId);
    if (results?.isSuccessful) {
      onRefresh();
      onClose();
    }
  };

  const handleDelete_No = () => {
    setOpenDeleteDialog(false);
  };

  const options = () => {
    return [{ name: "delete", iconName: "Delete" }];
  };

  const isTimeMileageLocked = () => {
    return data?.payrollLocked === true;
  };

  const isFuture = () => {
    const todayYyyymmdd = format(startOfToday(), "yyyy-MM-dd");
    if (typeof call?.yyyymmdd === "string" && call.yyyymmdd) {
      return call.yyyymmdd > todayYyyymmdd;
    }

    return call?.callStatus === "future";
  };

  const callDuration = () => {
    let text = "n/a";

    if (data.estCallDuration) {
      text = data.estCallDuration;

      if (data.callVariance) {
        text += ` +/- ${data.callVariance}`;
      }
    }

    return text;
  };

  const legacyHideTimeMileage =
    call?.callType === "training" || call?.callType === "webinar";
  const hasProjectType = !!call?.project?.projectType?.id;

  const projectTypeRules = {
    allowTime: hasProjectType
      ? call?.project?.projectType?.allowTime !== false
      : !legacyHideTimeMileage,
    allowMileage: hasProjectType
      ? call?.project?.projectType?.allowMileage !== false
      : !legacyHideTimeMileage,
    allowDuration: hasProjectType
      ? call?.project?.projectType?.allowDuration !== false
      : true,
  };

  if (!call || !data) return null;

  return (
    <>
      <Box>
        {!readOnly && (
          <EditorToolbar
            onSave={handleSave}
            message={call?.id}
            customOptions={options()}
            onCustomOption={handleCustomOption}
          />
        )}
        <Box sx={{ padding: "6px" }}>
          <Box
            sx={{
              paddingBottom: "4px",
              marginBottom: "8px",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box
                sx={{ display: "flex", alignItems: "center" }}
                gap={1}
              >
                <CallStatus status={call?.callStatus} />
                <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                  {`${call?.location?.name} ${
                    call?.location?.locationNumber
                      ? "#" + call?.location?.locationNumber
                      : ""
                  }`}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                  {data.repName ? data.repName : "Unassigned"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setUserModalOpen(true)}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Box>
            </Box>
            <Typography sx={{ fontSize: "10px" }}>
              {call?.location?.address?.streetAddress}
            </Typography>
            {call?.location?.address?.otherAddress && (
              <Typography sx={{ fontSize: "10px" }}>
                {call?.location?.address?.otherAddress}
              </Typography>
            )}
            <Typography sx={{ fontSize: "10px" }}>
              {`${call?.location?.address?.city}, ${call?.location?.address?.province}`}
            </Typography>
            <Typography sx={{ fontSize: "10px" }}>
              {call?.location?.address?.postal}
            </Typography>
            <Typography
              sx={{ fontSize: "8px" }}
            >{`location id: ${call?.location?.id}`}</Typography>
          </Box>

          <hr />

          <Box sx={{ marginTop: "12px" }}>
            <Box
              sx={{ display: "flex" }}
              gap={1}
            >
              <DateField
                id="callDate"
                label="Call Date"
                value={data.scheduleDate}
                onChange={(newValue) => handleChange("scheduleDate", newValue)}
                fullwidth={true}
                disabled={
                  !call?.project?.repsCanChange ||
                  call?.callStatus === "complete"
                }
              />
              <Input
                type="number"
                label="Mileage"
                value={data.mileage ? data.mileage.toString() : ""}
                onChange={(newValue) => handleChange("mileage", newValue)}
                width="100px"
                disabled={isTimeMileageLocked() || isFuture()}
                sx={projectTypeRules.allowMileage ? {} : { display: "none" }}
              />
              <Input
                type="number"
                label={getLabel("homeMileage")}
                value={
                  data.homeMileage !== "" &&
                  data.homeMileage !== null &&
                  data.homeMileage !== undefined
                    ? data.homeMileage.toString()
                    : ""
                }
                onChange={(newValue) => handleChange("homeMileage", newValue)}
                width="120px"
                disabled={isTimeMileageLocked() || isFuture()}
              />
              <Switch
                label="First Call"
                value={data.firstCall}
                onChange={(newValue) => handleChange("firstCall", newValue)}
                disabled={isFuture()}
                labelPosition="vertical"
              />
              <Switch
                label="Last Call"
                value={data.lastCall}
                onChange={(newValue) => handleChange("lastCall", newValue)}
                disabled={isFuture()}
                labelPosition="vertical"
              />
            </Box>

            <Box
              sx={{ display: "flex", alignItems: "center", marginTop: "6px" }}
              gap={1}
            >
              {data.isBilled && (
                <Box
                  sx={{
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  ✓ Billed
                </Box>
              )}
              <Switch
                label="Locked"
                value={data.isLocked}
                onChange={(newValue) => handleChange("isLocked", newValue)}
                labelPosition="vertical"
                disabled={readOnly}
              />
              <Switch
                label="Exclude Payroll"
                value={data.excludeFromPayroll}
                onChange={(newValue) =>
                  handleChange("excludeFromPayroll", newValue)
                }
                labelPosition="vertical"
                disabled={readOnly}
              />
              <Switch
                label="Exclude Billing"
                value={data.excludeFromBilling}
                onChange={(newValue) =>
                  handleChange("excludeFromBilling", newValue)
                }
                labelPosition="vertical"
                disabled={readOnly}
              />
              <Switch
                label="Sync Locked"
                value={data.syncLocked}
                onChange={(newValue) => handleChange("syncLocked", newValue)}
                labelPosition="vertical"
                disabled={readOnly}
              />
              {projectTypeRules.allowTime && (
                <>
                  <Time
                    label="Start Time"
                    fullwidth={true}
                    value={data.startedAt}
                    onChange={(newValue) => handleChange("startedAt", newValue)}
                    disabled={isTimeMileageLocked() || isFuture()}
                    allowSetNow={
                      !isTimeMileageLocked() && !isFuture()
                        ? handleStartCall
                        : false
                    }
                    dateValue={data.scheduleDate}
                  />

                  <Time
                    label="Finish Time"
                    fullwidth={true}
                    value={data.finishedAt}
                    onChange={(newValue) =>
                      handleChange("finishedAt", newValue)
                    }
                    disabled={isTimeMileageLocked() || isFuture()}
                    allowSetNow={
                      !isTimeMileageLocked() && !isFuture()
                        ? handleFinishCall
                        : false
                    }
                    dateValue={data.scheduleDate}
                  />
                </>
              )}
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "space-between" }}
              gap={1}
            >
              <Input
                label="Call Duration (est.)"
                value={callDuration()}
                disabled={true}
                fullwidth
              />
              <Input
                label="Duration (minutes)"
                type="number"
                value={data.callDuration ? data.callDuration.toString() : ""}
                onChange={(newValue) => handleChange("callDuration", newValue)}
                disabled={
                  isTimeMileageLocked() ||
                  isFuture() ||
                  !projectTypeRules.allowDuration
                }
                fullwidth
              />
              <Input
                label="Hourly Rate"
                type="number"
                value={data.hourlyRate}
                onChange={(newValue) => handleChange("hourlyRate", newValue)}
                disabled={false}
                fullwidth
              />
            </Box>

            <Box>
              <Input
                label="Rep notes"
                value={data.repNotes}
                onChange={(newValue) => handleChange("repNotes", newValue)}
                fullwidth
                multiline={true}
              />
            </Box>

            <hr />

            <Box>
              <Typography textAlign="center">Surveys</Typography>

              {Array.isArray(call?.surveys) && call?.surveys.length > 0 ? (
                <Stack spacing={1}>
                  {call?.surveys.map((survey) => {
                    const surveyId = survey.id || survey._id;
                    const displaySurveyStatus =
                      surveyStatusOverrides[surveyId] ?? survey.surveyStatus;
                    return (
                      <Box
                        key={surveyId}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          padding: "6px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                        onClick={() => handleOpenSurveyEntry(surveyId)}
                      >
                        <Box
                          sx={{ display: "flex" }}
                          gap={1}
                        >
                          <SurveyStatus surveyStatus={displaySurveyStatus} />
                          <Typography sx={{ fontSize: "14px" }}>
                            {survey.name}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography>No surveys available</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {openSurveyEntry && selectedSurveyId ? (
        <SurveyEntryModal
          surveyId={selectedSurveyId}
          callId={String(getId(call) || "")}
          mode="entry"
          open={openSurveyEntry}
          onClose={handleCloseSurveyEntry}
          onRefresh={onRefresh}
          isDisabled={isFuture()}
        />
      ) : null}

      {openDeleteDialog && (
        <Dialog_YesNo
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          title="Delete Call?"
          message="Everything connected with the call, including survey answers will be deleted!"
          onYes={handleDelete_Yes}
          onNo={handleDelete_No}
        />
      )}

      <UserSelectionModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSelect={(user) => {
          setData((current) => ({
            ...current,
            userId: user.id,
            repName: user.legalName || user.name,
          }));
          setUserModalOpen(false);
        }}
      />
    </>
  );
};

export default CallDocket;
